import { useEffect, useRef } from 'react';
import { request } from 'helpers/request';
import { CONSTANTS } from '../utils';

interface UseJobStatusPollingOptions {
	onComplete: () => void;
	interval?: number; // milliseconds
	maxAttempts?: number;
}

interface JobStatusResponse {
	job_id: string;
	status: 'processing' | 'completed' | 'failed';
}

/**
 * Custom hook to poll action-agent job status until completion.
 *
 * @param jobId - The job ID to poll
 * @param options - Polling configuration
 * @param options.onComplete - Callback when job completes successfully
 * @param options.interval - Polling interval in ms (default: 20000)
 * @param options.maxAttempts - Max polling attempts (default: 45)
 */
export const useJobStatusPolling = (
	jobId: string | null,
	options: UseJobStatusPollingOptions,
) => {
	const { onComplete, interval = 20000, maxAttempts = 45 } = options;
	const intervalRef = useRef<ReturnType<typeof setInterval>>();
	const attemptsRef = useRef(0);

	useEffect(() => {
		// Don't start polling if no job ID
		if (!jobId) {
			return;
		}

		// Reset attempts counter
		attemptsRef.current = 0;

		const pollJobStatus = async () => {
			try {
				// Check if max attempts reached
				if (attemptsRef.current >= maxAttempts) {
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
					}
					return;
				}

				attemptsRef.current += 1;

				// Fetch job status
				const response = await request({
					url: `${CONSTANTS.baseApiPath}/ml/action-agent/jobs/${jobId}`,
					method: 'GET',
				});

				const { status } = response.data;

				if (status === 'completed') {
					// Clear interval first
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
					}
					// Then call completion callback
					onComplete();
				} else if (status === 'failed') {
					console.error(`Job ${jobId}: Failed`);
					if (intervalRef.current) {
						clearInterval(intervalRef.current);
					}
				}
				// If still processing, continue polling
			} catch (error) {
				console.error(`Error polling job status for ${jobId}:`, error);
				// Continue polling even on error (could be transient)
			}
		};

		// Start polling immediately, then at intervals
		pollJobStatus();
		intervalRef.current = setInterval(pollJobStatus, interval);

		// Cleanup on unmount or when jobId changes
		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current);
			}
		};
	}, [jobId, interval, maxAttempts, onComplete]);
};
